import {useEffect, useRef, useState} from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import {backendWebsocketActionsConstants} from '../../constants';
import {systemHelpers} from '../../utils/helpers';
import {refreshToken} from '../../state/services/request';

const {OPEN} = ReadyState;

const {AUTH} = backendWebsocketActionsConstants;

const defaultOptions = systemHelpers.getWebsocketOptions();

const buildAccountingUrl = (baseUrl, token, companyId) => {
  const params = new URLSearchParams({
    'Authorization': `Bearer ${token}`,
    'company-id': companyId
  });
  return `${baseUrl}?${params.toString()}`;
};

// Calls callback(token, companyId) with a guaranteed non-expired token,
// refreshing first if the current one is expired or missing.
const withFreshToken = (callback) => {
  const {companyId, token, isExpired} = systemHelpers.getAuthDetails();
  if (!isExpired && token) return callback(token, companyId);
  refreshToken()
    .then(() => {
      const {companyId: freshCompanyId, token: freshToken} = systemHelpers.getAuthDetails();
      callback(freshToken, freshCompanyId);
    })
    .catch(() => {});
};

const WebSocket = ({onMessage, options, variant = 'backend'}) => {
  const baseUrl = systemHelpers.getWebsocketUrl(variant);
  const [socketUrl, setSocketUrl] = useState(() => {
    if (variant === 'accounting') return null;
    if (variant === 'openBanking' && baseUrl) {
      const {companyId, token} = systemHelpers.getAuthDetails();
      const params = new URLSearchParams({company_id: companyId, token});
      return `${baseUrl}?${params.toString()}`;
    }
    return baseUrl;
  });

  useEffect(() => {
    if (variant !== 'accounting' || !baseUrl) return;
    withFreshToken((token, companyId) => setSocketUrl(buildAccountingUrl(baseUrl, token, companyId)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    getWebSocket,
    lastJsonMessage,
    readyState,
    sendJsonMessage
  } = useWebSocket(socketUrl, {...defaultOptions, ...options});
  const [authorized, setAuthorized] = useState(false);
  const isRefreshing = useRef(false);

  useEffect(() => {
    if (variant === 'accounting' || variant === 'openBanking' || readyState !== OPEN) return;
    withFreshToken((token, companyId) => sendJsonMessage({
      'action': AUTH,
      'Authorization': `Bearer ${token}`,
      'CompanyId': companyId
    }));
  }, [readyState]); // eslint-disable-line react-hooks/exhaustive-deps

  // close websocket after unmount
  useEffect(() => () => {
    getWebSocket()?.close();
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!lastJsonMessage || typeof lastJsonMessage !== 'object') return;

    if ((variant === 'accounting' || variant === 'openBanking') && onMessage) {
      onMessage(lastJsonMessage);
      return;
    }

    const isAuthAction = lastJsonMessage.action === AUTH;

    if (isAuthAction && !lastJsonMessage.state && !isRefreshing.current) {
      isRefreshing.current = true;
      refreshToken()
        .then(() => getWebSocket()?.close())
        .catch(() => {})
        .finally(() => { isRefreshing.current = false; });
      return;
    }

    if (!authorized && isAuthAction) {
      setAuthorized(lastJsonMessage.state);
      return;
    }

    onMessage?.(lastJsonMessage);
  }, [lastJsonMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

export default WebSocket;
